import {Injectable, isDevMode, NgZone, OnDestroy} from '@angular/core';
import {HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel, RetryContext} from '@microsoft/signalr';
import {EnvService} from '@services/env.service';
import {ClientAlertModel, NewsAlertModel} from '@shared/models/news-alert.model';
import {
  AlertMessageService
} from '@harmony-modules/configuration/sections/employee/employee-import/shared/alert-message-service/alert-message.service';
import {AuthenticationService} from '@shared/services';
import {filter} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SignalRService implements OnDestroy {
  /* eslint-disable no-console */
  private _hubConnection: HubConnection;
  private _reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private _isInitialized = false;

  constructor(
    private envService: EnvService,
    private alert: AlertMessageService,
    private ngZone: NgZone,
    private authService: AuthenticationService
  ) {
    this.authService.currentUser.pipe(filter(res => !res)).subscribe(() => this.disconnect());
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  initialize(): void {
    console.log('SignalR: initialize() called');
    console.log('SignalR: Admin URL:', this.envService.adminUrl);

    // Prevent multiple initializations causing connection leaks
    if (this._isInitialized && this._hubConnection) {
      console.log('SignalR already initialized, skipping...');
      return;
    }

    // Clean up existing connection if any
    if (this._hubConnection) {
      this.cleanupConnection();
    }

    const sid = this.authService.currentUserValue?.id;
    const companyId = this.authService.currentUserValue?.selectedCompany?.Id;
    const hubUrl = `${this.envService.adminUrl}/hub/notification?companyId=${companyId}&sid=${sid}}`;
    console.log('SignalR: Connecting to:', hubUrl);

    this._hubConnection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .configureLogging(isDevMode() ? LogLevel.Information : LogLevel.Error)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
          if (retryContext.retryReason) {
            switch (retryContext.previousRetryCount) {
              case 0:
                return 0;
              case 1:
                return 5000;
              case 2:
                return 10000;
              case 3:
                return 20000;
              default:
                return 30000;
            }
          }
          return null;
        }
      })
      .build();

    this._hubConnection.onclose(async (error) => {
      console.debug(new Date().toLocaleString(), ': Disconnected from Notification Hub');
      if (error && this._isInitialized) {
        this.reconnect(30000);
      }
    });

    this._isInitialized = true;
    this.connect();
    this.listenToMessages();
  }

  connect(): void {
    if (!this._hubConnection) {
      console.debug('No hub connection available. Call initialize() first.');
      return;
    }

    if (this._hubConnection.state !== HubConnectionState.Disconnected) {
      console.debug('Connection is not in Disconnected state, current state:', this._hubConnection.state);
      return;
    }

    try {
      this._hubConnection.start()
        .then(() => {
          console.log(new Date().toLocaleString(), ': SignalR Connected to Broadcast Hub');
        })
        .catch((err) => {
          console.error(new Date().toLocaleString(), ': SignalR Failed to connect to Broadcast Hub', err);
          if (this._isInitialized) {
            this.reconnect(30000);
          }
        });
    } catch (e) {
      console.error(new Date().toLocaleString(), ': SignalR Failed to locate the server', e);
      if (this._isInitialized) {
        this.reconnect(60000);
      }
    }
  }

  private listenToMessages(): void {
    // Register handler for 'deliver' method from backend
    this._hubConnection.on('deliver', (message: NewsAlertModel) => {
      // Run inside Angular zone to trigger change detection
      this.ngZone.run(() => {
        this.alert.showNewsAlert(message);
      });
    });
    this._hubConnection.on('ClientNotification', (message: ClientAlertModel) => {
      // Run inside Angular zone to trigger change detection
      this.ngZone.run(() => {
        this.alert.showClientAlert(message);
      });
    });

    // Log reconnection events for debugging
    this._hubConnection.onreconnecting((error) => {
      console.log('SignalR: Reconnecting...', error);
    });

    this._hubConnection.onreconnected((connectionId) => {
      console.log('SignalR: Reconnected with connectionId:', connectionId);
    });
  }

  private reconnect(timeOut = 10000): void {
    // Clear any existing reconnect timeout to prevent multiple reconnect attempts
    this.clearReconnectTimeout();

    if (!this._isInitialized) {
      console.debug('Service is not initialized, skipping reconnect');
      return;
    }

    const message = `Server got disconnected. Trying to reconnect in ${timeOut / 1000} seconds`;
    console.debug(message);

    this._reconnectTimeout = setTimeout(() => {
      this._reconnectTimeout = null;
      if (this._isInitialized) {
        console.debug('Reconnecting to Broadcast Hub.....');
        this.connect();
      }
    }, timeOut);
  }

  private clearReconnectTimeout(): void {
    if (this._reconnectTimeout) {
      clearTimeout(this._reconnectTimeout);
      this._reconnectTimeout = null;
    }
  }

  private cleanupConnection(): void {
    this.clearReconnectTimeout();
    if (this._hubConnection) {
      // Remove event handler to prevent memory leaks
      this._hubConnection.off('deliver');
      this._hubConnection.stop().catch(() => {
        // Ignore errors during cleanup
      });
    }
  }

  async disconnect(): Promise<void> {
    this._isInitialized = false;
    this.clearReconnectTimeout();

    if (this._hubConnection) {
      this._hubConnection.off('deliver');
      await this._hubConnection.stop().catch(() => {
        // Ignore errors during disconnect
      });
    }
  }

  get hubConnection(): HubConnection {
    return this._hubConnection;
  }
}
